import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Component, Inject } from '@angular/core';
import { forkJoin } from 'rxjs';
import { PublisherNote } from '../../../shared/domain/publisher-note.model';
import { SnackBarService } from '../../../shared/services/snackbar.service';
import { GlobalSpinnerService } from '../../../shared/components/loader-service/global-spinner.service';
import { AdStatsPublishersService } from '../../services/ad-stats-publishers.service';
import { ServerErrorUtils } from '../../../shared/utils/server-error-utils';
import { FileAttachment } from '../../../shared/domain/file-attachment';

@Component({
  selector: 'cm-note-with-file-editor-dialog',
  template: `
    <h3 mat-dialog-title [hidden]="publisherNote.id">Write a new note</h3>
    <h3 mat-dialog-title [hidden]="!publisherNote.id">Edit note</h3>
    <mat-dialog-content>
          <mat-form-field floatLabel="always" class="w-100">
              <textarea matInput name="notes" required [(ngModel)]="publisherNote.note" style="min-height: 150px "
                        placeholder="Note" class="textarea-note"></textarea>
          </mat-form-field>
    </mat-dialog-content>
    <cm-custom-upload-multiple-button class="d-block mb-4"
                                      [initialFilesList]="publisherNote.fileAttachments"
                                      [emitInitialFileChange]="true"
                                      (filesChange)="filesChange($event)"
                                      label="File upload">
    </cm-custom-upload-multiple-button>
    <mat-dialog-actions class="justify-content-center">
	    <button mat-raised-button [mat-dialog-close]="false">Close</button>
      <button mat-raised-button
              color="primary"
              class="mr-1"
              [disabled]="!publisherNote.note || !publisherNote.note.length"
              (click)="save()">Save</button>
    </mat-dialog-actions>
  `,
})
export class NoteWithFileEditorDialogComponent {
  publisherNote: PublisherNote;
  selectedFiles: File[] = [];

  constructor(public dialogRef: MatDialogRef<NoteWithFileEditorDialogComponent>,
    private gss: GlobalSpinnerService,
    private sbs: SnackBarService,
    private service: AdStatsPublishersService,
    @Inject(MAT_DIALOG_DATA) public data: any) {

    this.publisherNote = data.note;
  }


  save() {
    this.publisherNote.id ? this.updateNote() : this.createNote();
  }

  createNote() {
    this.gss.showLoader();
    this.service.createPublisherNotes(this.publisherNote.publisherId, this.publisherNote).subscribe(next => {
      this.publisherNote.id = next.id;
      if (this.selectedFiles.length > 0) {
        this.uploadFiles();
      } else {
        this.gss.hideLoader();
        this.sbs.success('Publisher note has been successfully created.');
        this.dialogRef.close(true);
      }
    }, error => {
      this.gss.hideLoader();
      ServerErrorUtils.serverValidationOrMsg(error, this.sbs, 'Creating note has failed.');
      this.dialogRef.close(true);
    });
  }

  uploadFiles() {
    const observables = this.selectedFiles.map(item => this.service.uploadPublisherNoteFile(item, this.publisherNote.id));
    forkJoin(observables).subscribe(next => {
      this.gss.hideLoader();
      this.sbs.success('Publisher note has been successfully created.');
      this.dialogRef.close(true);
    }, error => {
      this.gss.hideLoader();
      this.sbs.error('Error while uploading files.');
      this.dialogRef.close(true);
    });
  }

  updateNote() {
    this.gss.showLoader();
    this.service.updatePublisherNotes(this.publisherNote).subscribe(next => {
      this.updateFiles();
    }, error => {
      this.gss.hideLoader();
      this.sbs.error('Updating note has failed.');
    });
  }

  updateFiles() {
    const filesToUpload = this.getFilesToUpload();
    const filesToDelete = this.getFilesToDelete();
    if (filesToUpload.length === 0 && filesToDelete.length === 0) {
      this.gss.hideLoader();
      this.sbs.success('Publisher note has been successfully updated.');
      this.dialogRef.close(true);
      return;
    }

    const observables = [];
    filesToUpload.forEach(item => observables.push(this.service.uploadPublisherNoteFile(item, this.publisherNote.id)));
    filesToDelete.forEach(item => observables.push(this.service.deletePublisherNoteFile(item.name, this.publisherNote.id)));

    forkJoin(observables).subscribe(next => {
      this.gss.hideLoader();
      this.sbs.success('Publisher note has been successfully updated.');
      this.dialogRef.close(true);
    }, error => {
      this.gss.hideLoader();
      this.sbs.error('Error while uploading files.');
      this.dialogRef.close(true);
    });
  }

  // upload only new files which are not in activity
  getFilesToUpload(): File[] {
    return this.selectedFiles.filter(newFile => {
      return !this.publisherNote.fileAttachments.some(currentFile => currentFile.name === newFile.name);
    });
  }

  getFilesToDelete(): FileAttachment[] {
    return this.publisherNote.fileAttachments.filter(currentFile => {
      return !this.selectedFiles.some(newFile => newFile.name === currentFile.name);
    });
  }

  filesChange(event) {
    this.selectedFiles = event;
  }

}
